import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  FiClipboard,
  FiCheck,
  FiSend,
  FiPenTool,
  FiArrowRight,
  FiArrowLeft,
  FiRotateCcw,
} from 'react-icons/fi';
import api, { downloadAuthenticatedFile } from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import LoadingButton from '../components/LoadingButton';
import ListSearchBar from '../components/ListSearchBar';
import { FadeIn } from '../components/motion';
import { StatusBadge } from '../components/shared/Ui';
import { formatDateOnly, formatLocalDateTime } from '../utils/formatTimestamp';
import formatMoney from '../utils/formatMoney';
import { ORDER_STATUS_LABEL } from '../utils/orderStatus';
import './Services.css';
import './Requisitions.css';
import { appAlert, appConfirm } from '../utils/appDialog';
import ListPager from '../components/ListPager';
import { useClientPager } from '../hooks/useClientPager';

const STATUS_LABEL = ORDER_STATUS_LABEL;

const STAGE_RAIL = ['draft', 'signature', 'invoicing'];

const money = formatMoney;

function OrdersListToolbar({ search, onSearch, statusFilter, onStatusFilter }) {
  return (
    <div className="list-filters-panel list-toolbar-panel">
      <div className="filter-row list-toolbar-row">
        <div className="filter-group filter-group--wide list-toolbar-search">
          <ListSearchBar
            id="orders-filter-search"
            value={search}
            onChange={onSearch}
            placeholder="Search number, title, customer…"
          />
        </div>
        <div className="filter-group">
          <label htmlFor="orders-filter-status">Status</label>
          <select
            id="orders-filter-status"
            value={statusFilter}
            onChange={(e) => onStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABEL).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

const Orders = () => {
  const { hasAnyPermission, hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  // Mirrors canManage/canSign/canView in backend/src/routes/purchaseOrders.js.
  const canManage = hasAnyPermission(['order_manage', 'view_all_data']);
  const canSign = hasPermission('order_sign') || canManage;
  const canView =
    hasAnyPermission(['tab_orders', 'order_view']) || canManage || canSign;

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [selectedId, setSelectedId] = useState(routeId || searchParams.get('id') || null);
  const [comment, setComment] = useState('');
  const [externalSigner, setExternalSigner] = useState('');
  const [draftForm, setDraftForm] = useState({
    title: '',
    effective_date: '',
    end_date: '',
    notes: '',
  });

  // Deep link: /orders/:id preferred; keep legacy ?id= in sync by redirecting once.
  useEffect(() => {
    if (routeId) {
      setSelectedId(String(routeId));
      return;
    }
    const qid = searchParams.get('id');
    if (qid) {
      navigate(`/orders/${qid}`, { replace: true });
    } else {
      setSelectedId(null);
    }
  }, [routeId, searchParams, navigate]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const openRecord = (id) => {
    const next = String(id);
    setSelectedId(next);
    navigate(`/orders/${next}`);
  };

  const backToList = () => {
    setSelectedId(null);
    navigate('/orders');
  };

  const { data: list = [], isLoading, isError: listError, error: listErrorObj } = useQuery(
    ['purchase-orders', statusFilter, searchDebounced],
    () =>
      api
        .get('/purchase-orders', {
          params: {
            status: statusFilter || undefined,
            q: searchDebounced || undefined,
          },
        })
        .then((r) => r.data),
    { keepPreviousData: true }
  );

  const {
    pageItems: pagedList,
    pagination: listPagination,
    setPage: setListPage,
  } = useClientPager(list, {
    resetKey: `${statusFilter}|${searchDebounced}`,
  });

  const { data: detail, isLoading: detailLoading } = useQuery(
    ['purchase-order', selectedId],
    () => api.get(`/purchase-orders/${selectedId}`).then((r) => r.data),
    { enabled: Boolean(selectedId) }
  );

  // Seed the draft form once per record. Background refetches return a new
  // object identity, which would otherwise discard in-progress edits.
  const seededDraftIdRef = useRef(null);
  useEffect(() => {
    if (!detail) return;
    if (seededDraftIdRef.current === String(detail.id)) return;
    seededDraftIdRef.current = String(detail.id);
    setDraftForm({
      title: detail.title || '',
      effective_date: detail.effective_date ? String(detail.effective_date).slice(0, 10) : '',
      end_date: detail.end_date ? String(detail.end_date).slice(0, 10) : '',
      notes: detail.notes || '',
    });
  }, [detail]);

  const transitionMutation = useMutation(
    ({ id, action, comment: c, external_signed_by }) =>
      api.post(`/purchase-orders/${id}/transition`, {
        action,
        comment: c,
        external_signed_by,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('purchase-orders');
        queryClient.invalidateQueries(['purchase-order', selectedId]);
        setComment('');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const invoiceMutation = useMutation(
    ({ id, send_email }) =>
      api.post(`/purchase-orders/${id}/generate-invoice`, { send_email }),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries('purchase-orders');
        queryClient.invalidateQueries(['purchase-order', selectedId]);
        const d = res.data || {};
        void appAlert(
          d.already_existed
            ? `Invoice ${d.document?.number || ''} already exists.`
            : `Invoice ${d.document?.number || ''} created.${
                d.emails_queued ? ` Email queued to ${d.emails_queued} recipient(s).` : ''
              }`
        );
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const saveMutation = useMutation(
    ({ id, payload }) => api.put(`/purchase-orders/${id}`, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('purchase-orders');
        queryClient.invalidateQueries(['purchase-order', selectedId]);
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const activeStageIdx = useMemo(() => {
    const status = detail?.status;
    if (!status) return -1;
    if (['completed', 'executed', 'expired'].includes(status)) return STAGE_RAIL.length;
    return STAGE_RAIL.indexOf(status);
  }, [detail?.status]);

  const doTransition = (action, extra = {}) => {
    if (!detail) return;
    transitionMutation.mutate({
      id: detail.id,
      action,
      comment: comment || undefined,
      ...extra,
    });
  };

  return (
    <div className="orders-page requisitions-page list-page">
      {!canView ? (
        <div className="error" role="alert">
          You do not have permission to view Client POs.
        </div>
      ) : (
        <>
      <FadeIn className="page-header">
        <div className="page-header-text">
          <h1>
            <FiClipboard aria-hidden /> Client POs
          </h1>
          <p className="page-subtitle">
            Customer engagement orders from approved requisitions. Distinct from Vendor POs under
            Buying.
          </p>
        </div>
      </FadeIn>

      {!selectedId ? (
        <section className="svc-panel req-table-panel">
          <div className="svc-panel-head">
            <div>
              <h2>Client POs</h2>
              <p className="svc-panel-meta">
                {list.length} {list.length === 1 ? 'Client PO' : 'Client POs'}
              </p>
            </div>
            <Link className="btn btn-secondary btn-sm" to="/requisitions">
              Open requisitions
            </Link>
          </div>

          <OrdersListToolbar
            search={search}
            onSearch={setSearch}
            statusFilter={statusFilter}
            onStatusFilter={setStatusFilter}
          />

          {listError ? (
            <div className="error" role="alert">
              {listErrorObj?.response?.data?.error || listErrorObj?.message || 'Failed to load Client POs.'}
            </div>
          ) : isLoading ? (
            <div className="loading">Loading…</div>
          ) : list.length === 0 ? (
            <div className="svc-empty-inline">
              <FiClipboard aria-hidden />
              <p>No Client POs yet. Approve a requisition to draft one.</p>
              <Link className="btn btn-primary btn-sm" to="/requisitions">
                Open requisitions
              </Link>
            </div>
          ) : (
            <div className="svc-table-scroll">
              <table className="req-table svc-table">
                <thead>
                  <tr>
                    <th scope="col">Number</th>
                    <th scope="col">Title</th>
                    <th scope="col">Customer</th>
                    <th scope="col">Status</th>
                    <th scope="col">Currency</th>
                    <th scope="col">Value</th>
                    <th scope="col">Requisition</th>
                    <th scope="col">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedList.map((row) => (
                    <tr
                      key={row.id}
                      tabIndex={0}
                      role="link"
                      aria-label={`Open Client PO ${row.number}`}
                      onClick={() => openRecord(row.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openRecord(row.id);
                        }
                      }}
                    >
                      <td className="req-cell-title">
                        <span className="erp-code">{row.number}</span>
                      </td>
                      <td>{row.title || row.requisition_title || '—'}</td>
                      <td>{row.customer_billing_name || '—'}</td>
                      <td>
                        <StatusBadge
                          status={row.status}
                          label={STATUS_LABEL[row.status] || row.status}
                        />
                      </td>
                      <td>
                        <span className="erp-code">{row.currency || 'USD'}</span>
                      </td>
                      <td className="money-cell">
                        {row.estimated_value != null
                          ? money(row.estimated_value, row.currency)
                          : '—'}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {row.requisition_id ? (
                          <Link to={`/requisitions?id=${row.requisition_id}`}>
                            {row.requisition_title || `#${row.requisition_id}`}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {row.sales_document_id ? (
                          <Link to={`/sales/documents/${row.sales_document_id}`}>
                            {row.sales_document_number || `#${row.sales_document_id}`}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!listError && !isLoading && list.length > 0 ? (
            <ListPager
              page={listPagination.page}
              pageCount={listPagination.pageCount}
              total={listPagination.total}
              limit={listPagination.limit}
              onPageChange={setListPage}
            />
          ) : null}
        </section>
      ) : (
        <section className="svc-panel req-detail-view">
          {detailLoading ? (
            <div className="loading">Loading…</div>
          ) : !detail ? (
            <>
              <button type="button" className="btn btn-secondary btn-sm" onClick={backToList}>
                <FiArrowLeft /> Back to Client POs
              </button>
              <div className="svc-empty-inline">
                <p>Client PO not found.</p>
              </div>
            </>
          ) : (
            <>
              <div className="req-detail-top">
                <div>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={backToList}>
                    <FiArrowLeft /> Back to Client POs
                  </button>
                  <h2>
                    <span className="erp-code">{detail.number}</span>
                  </h2>
                  <p className="svc-panel-meta">
                    {detail.title}
                    {detail.customer_billing_name ? ` · ${detail.customer_billing_name}` : ''}
                    {detail.estimated_value != null
                      ? ` · ${money(detail.estimated_value, detail.currency)}`
                      : ''}
                    {detail.currency ? ` · ${detail.currency}` : ''}
                  </p>
                </div>
                <StatusBadge
                  status={detail.status}
                  label={STATUS_LABEL[detail.status] || detail.status}
                />
              </div>

              <ul className="req-stage-track req-stage-track--3">
                {STAGE_RAIL.map((key, idx) => (
                  <li
                    key={key}
                    className={`req-stage${idx <= activeStageIdx ? ' is-done' : ''}${
                      detail.status === key ? ' is-current' : ''
                    }`}
                  >
                    {STATUS_LABEL[key]}
                  </li>
                ))}
              </ul>

              <div className="req-related-links">
                <Link className="req-related-chip" to={`/requisitions?id=${detail.requisition_id}`}>
                  Requisition <strong>{detail.requisition_title || `#${detail.requisition_id}`}</strong>
                </Link>
                {detail.sales_document_id ? (
                  <Link
                    className="req-related-chip"
                    to={`/sales/documents/${detail.sales_document_id}`}
                  >
                    Invoice <strong>{detail.sales_document_number || `#${detail.sales_document_id}`}</strong>
                  </Link>
                ) : null}
              </div>

              {detail.status === 'draft' && canManage ? (
                <form
                  className="req-form"
                  style={{ marginBottom: '1rem' }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveMutation.mutate({ id: detail.id, payload: draftForm });
                  }}
                >
                  <div className="svc-form-grid svc-form-grid--2">
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        value={draftForm.title}
                        onChange={(e) => setDraftForm((f) => ({ ...f, title: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Effective date</label>
                      <input
                        type="date"
                        value={draftForm.effective_date}
                        onChange={(e) =>
                          setDraftForm((f) => ({ ...f, effective_date: e.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>End date</label>
                      <input
                        type="date"
                        value={draftForm.end_date}
                        onChange={(e) => setDraftForm((f) => ({ ...f, end_date: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      rows={2}
                      value={draftForm.notes}
                      onChange={(e) => setDraftForm((f) => ({ ...f, notes: e.target.value }))}
                    />
                  </div>
                  <LoadingButton
                    type="submit"
                    className="btn btn-secondary"
                    loading={saveMutation.isLoading}
                  >
                    Save draft
                  </LoadingButton>
                </form>
              ) : (
                <dl className="req-dl">
                  <div>
                    <dt>Effective</dt>
                    <dd>{formatDateOnly(detail.effective_date)}</dd>
                  </div>
                  <div>
                    <dt>End</dt>
                    <dd>{formatDateOnly(detail.end_date)}</dd>
                  </div>
                  <div>
                    <dt>Internal signed</dt>
                    <dd>
                      {detail.internal_signed_at
                        ? `${formatLocalDateTime(detail.internal_signed_at)}${
                            detail.internal_signer_first_name
                              ? ` · ${detail.internal_signer_first_name} ${detail.internal_signer_last_name || ''}`
                              : ''
                          }`
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt>External signed</dt>
                    <dd>
                      {detail.external_signed_at
                        ? `${formatLocalDateTime(detail.external_signed_at)}${
                            detail.external_signed_by ? ` · ${detail.external_signed_by}` : ''
                          }`
                        : '—'}
                    </dd>
                  </div>
                </dl>
              )}

              <h3>Packages</h3>
              {(detail.packages || []).length === 0 ? (
                <p className="svc-empty">No packages on this Client PO.</p>
              ) : (
                <ul className="req-list-items">
                  {(detail.packages || []).map((p) => (
                    <li key={p.id} className="req-list-item" style={{ cursor: 'default' }}>
                      <strong>{p.package_name}</strong>
                      <span className="svc-panel-meta">
                        Qty {p.quantity} · {money(p.unit_price, p.currency)} each ·{' '}
                        {money((Number(p.quantity) || 0) * (Number(p.unit_price) || 0), p.currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="req-detail-actions" style={{ flexWrap: 'wrap', marginTop: '1rem' }}>
                {detail.status === 'draft' && canManage && (
                  <LoadingButton
                    type="button"
                    className="btn btn-primary"
                    loading={transitionMutation.isLoading}
                    onClick={() => doTransition('submit_for_signature')}
                  >
                    <FiSend /> Submit for signature
                  </LoadingButton>
                )}
                {['draft', 'signature'].includes(detail.status) &&
                  !detail.internal_signed_at &&
                  canSign && (
                    <LoadingButton
                      type="button"
                      className="btn btn-primary"
                      loading={transitionMutation.isLoading}
                      onClick={() => doTransition('sign_internal')}
                    >
                      <FiPenTool /> Mark internal signed
                    </LoadingButton>
                  )}
                {detail.status === 'signature' &&
                  detail.internal_signed_at &&
                  !detail.external_signed_at &&
                  canSign && (
                    <>
                      <label className="form-group" style={{ minWidth: '12rem', margin: 0 }}>
                        <span className="visually-hidden">External signatory name</span>
                        <input
                          type="text"
                          placeholder="External signatory name"
                          aria-label="External signatory name"
                          value={externalSigner}
                          onChange={(e) => setExternalSigner(e.target.value)}
                        />
                      </label>
                      <LoadingButton
                        type="button"
                        className="btn btn-primary"
                        loading={transitionMutation.isLoading}
                        onClick={() =>
                          doTransition('sign_external', {
                            external_signed_by: externalSigner || undefined,
                          })
                        }
                      >
                        <FiCheck /> Mark external signed
                      </LoadingButton>
                    </>
                  )}
                {detail.status === 'signature' &&
                  detail.internal_signed_at &&
                  detail.external_signed_at &&
                  canManage && (
                    <LoadingButton
                      type="button"
                      className="btn btn-primary"
                      loading={transitionMutation.isLoading}
                      onClick={() => doTransition('start_invoicing')}
                    >
                      <FiArrowRight /> Move to invoicing
                    </LoadingButton>
                  )}
                {detail.status === 'invoicing' && canManage && !detail.sales_document_id && (
                  <LoadingButton
                    type="button"
                    className="btn btn-primary"
                    loading={invoiceMutation.isLoading}
                    onClick={() => invoiceMutation.mutate({ id: detail.id, send_email: true })}
                  >
                    Generate invoice &amp; email PDF
                  </LoadingButton>
                )}
                {detail.status === 'invoicing' && canManage && detail.sales_document_id && (
                  <LoadingButton
                    type="button"
                    className="btn btn-primary"
                    loading={transitionMutation.isLoading}
                    onClick={() => doTransition('acknowledge_invoice')}
                  >
                    <FiCheck /> Acknowledge invoice
                  </LoadingButton>
                )}
                {canManage &&
                  (detail.status !== 'draft' ||
                    detail.sales_document_id ||
                    detail.internal_signed_at) && (
                    <LoadingButton
                      type="button"
                      className="btn btn-danger"
                      loading={transitionMutation.isLoading}
                      onClick={async () => {
                        if (
                          !await appConfirm(
                            'Revoke this Client PO? It will reset to Draft, clear signatures, and void any linked invoice so a new invoice can be generated after re-signing.'
                          )
                        ) {
                          return;
                        }
                        doTransition('revoke');
                      }}
                    >
                      <FiRotateCcw /> Revoke
                    </LoadingButton>
                  )}
              </div>

              {detail.sales_document_id ? (
                <div className="req-section" style={{ marginTop: '0.75rem' }}>
                  <h3>Invoice</h3>
                  <div className="req-related-links">
                    <Link
                      className="req-related-chip"
                      to={`/sales/documents/${detail.sales_document_id}`}
                    >
                      Invoice <strong>{detail.sales_document_number || `#${detail.sales_document_id}`}</strong>
                    </Link>
                    {detail.sales_pdf_r2_key ? (
                      <button
                        type="button"
                        className="req-related-chip"
                        onClick={async () => {
                          try {
                            await downloadAuthenticatedFile(
                              `/services/sales/documents/${detail.sales_document_id}/pdf`,
                              `${detail.sales_document_number || 'invoice'}.pdf`
                            );
                          } catch (e) {
                            void appAlert(e.response?.data?.error || e.message || 'Failed to download PDF');
                          }
                        }}
                      >
                        Download PDF
                      </button>
                    ) : null}
                  </div>
                  {detail.invoice_acknowledged_at ? (
                    <p className="svc-panel-meta">
                      Acknowledged {formatLocalDateTime(detail.invoice_acknowledged_at)}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {detail.status === 'invoicing' && !detail.sales_document_id ? (
                <p className="req-banner" style={{ marginTop: '1rem' }}>
                  Generate a customer invoice (INV-*) from packages. Stakeholders receive the PDF by
                  email. Consultant payout documents (PAY-*) stay under Finance → Consultant payouts
                  (Payout invoices tab).
                  and are not customer revenue.
                </p>
              ) : null}

              {(canManage || canSign) && (
                <label style={{ display: 'block', marginTop: '0.75rem' }}>
                  Comment (optional)
                  <input
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Note for history…"
                  />
                </label>
              )}

              {(detail.events || []).length > 0 && (
                <>
                  <h3 style={{ marginTop: '1.25rem' }}>History</h3>
                  <ul className="req-list-items">
                    {detail.events.map((ev) => (
                      <li key={ev.id} className="req-list-item" style={{ cursor: 'default' }}>
                        <strong>
                          {ev.action}
                          {ev.from_status ? ` · ${ev.from_status} → ${ev.to_status}` : ` · ${ev.to_status}`}
                        </strong>
                        <span className="svc-panel-meta">
                          {formatLocalDateTime(ev.createdon_timestamp)}
                          {ev.actor_first_name
                            ? ` · ${ev.actor_first_name} ${ev.actor_last_name || ''}`
                            : ''}
                          {ev.comment ? ` — ${ev.comment}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </section>
      )}
        </>
      )}
    </div>
  );
};

export default Orders;
